import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid,
} from 'recharts';
import { useInventorySource } from './InventorySource';
import LoadingSpinner from '../Shared/LoadingSpinner';
import { fmtMoney, fmtNumber, fmtMonth, fmtDate } from './format';
import DataDisclosure from './DataDisclosure';
import { c, chart, tbl } from '../../theme';

// Monthly value trend for one cost center (or the total across accessible
// cost centers when ccId is omitted). Every daily snapshot is retained, so
// each bar is ONE snapshot — the last of its month — never a sum of the days
// in it: a stock position is a level, not a flow.
export default function InventoryTrend({ ccId, ccName }) {
  const { api, queryKey } = useInventorySource();
  const { data, isLoading, error } = useQuery({
    queryKey: queryKey('inventory-trend', ccId || 'all'),
    queryFn:  () => api.get('/inventory/trend', { params: ccId ? { cc_id: ccId } : {} }),
  });

  if (isLoading) return <div style={s.wrap}><LoadingSpinner /></div>;
  if (error)     return <div style={s.wrap}><div style={s.err}>{error.message}</div></div>;

  const months = data?.months || [];
  if (!months.length) {
    return (
      <div style={s.wrap}>
        <div style={s.empty}>No prior snapshots yet — history builds up as monthly imports are committed.</div>
      </div>
    );
  }

  const chartData = months.map((m) => ({ ...m, shortLabel: fmtMonth(m.snapshot_month) }));
  const latest = chartData[chartData.length - 1];

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <div style={s.title}>{ccName ? `${ccName} — Value Trend` : 'Total Value Trend'}</div>
        <div style={s.subtitle}>
          {months.length === 1
            ? 'One snapshot so far — history builds from here'
            : `${months.length} months — the last snapshot in each`}
        </div>
      </div>

      <div style={s.chartWrap}>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={chartData} margin={{ top: 8, right: 16, bottom: 4, left: 8 }}>
            <CartesianGrid vertical={false} stroke={chart.grid} />
            <XAxis dataKey="shortLabel" tick={{ fontSize: 10, fill: chart.axisText }} axisLine={false} tickLine={false} />
            <YAxis
              tickFormatter={fmtMoney}
              tick={{ fontSize: 10, fill: chart.axisText }}
              axisLine={false} tickLine={false} width={64}
            />
            <Tooltip
              cursor={{ fill: 'rgba(27,42,74,0.05)' }}
              formatter={(v) => [fmtMoney(v), 'Inventory Value']}
              labelFormatter={(_l, p) => p?.[0]?.payload?.shortLabel || ''}
              contentStyle={{ fontSize: '12px', borderRadius: '8px', border: `1px solid ${chart.tooltipBorder}` }}
            />
            {/* Capped, or a single retained month renders as one bar the full
                width of the panel, which reads as a broken chart rather than
                as a series with one point in it. */}
            <Bar dataKey="total_value" radius={[4, 4, 0, 0]} maxBarSize={64} isAnimationActive={false}>
              {chartData.map((m) => (
                <Cell key={m.snapshot_month} fill={m.snapshot_month === latest.snapshot_month ? chart.emphasis : chart.recessive} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {latest && (
        <div style={s.strip}>
          <div style={s.stripItem}>
            <div style={s.stripLabel}>Latest Month</div>
            <div style={s.stripVal}>{latest.shortLabel}</div>
          </div>
          <div style={s.stripItem}>
            <div style={s.stripLabel}>Value</div>
            <div style={s.stripVal}>{fmtMoney(latest.total_value)}</div>
          </div>
          {/* Lines and parts, side by side. One part held at three yards is
              three lines — the gap between these two is how much duplication
              the catalogue is carrying, which is the question the cross-yard
              panel answers in detail. */}
          <div style={s.stripItem}>
            <div style={s.stripLabel}>Part Lines</div>
            <div style={s.stripVal}>{fmtNumber(latest.item_count)}</div>
          </div>
          <div style={s.stripItem}>
            <div style={s.stripLabel}>Distinct Parts</div>
            <div style={s.stripVal}>{fmtNumber(latest.distinct_part_count)}</div>
          </div>
        </div>
      )}

      {/* The snapshot DATE is the column that matters and the only one the
          chart cannot show: bars are labeled by month, but each is one
          snapshot — the last in that month — so "Aug" may mean the 13th. A
          reader comparing this against a workbook needs to know which day. */}
      <DataDisclosure label="month" count={chartData.length}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Month</th>
              <th style={s.th}>Snapshot Date</th>
              <th style={s.thR}>Part Lines</th>
              <th style={s.thR}>Distinct Parts</th>
              <th style={s.thR}>Value</th>
              <th style={s.thR}>Change</th>
            </tr>
          </thead>
          <tbody>
            {chartData.map((m, i) => {
              const prev   = i > 0 ? Number(chartData[i - 1].total_value || 0) : null;
              const change = prev === null ? null : Number(m.total_value || 0) - prev;
              return (
                <tr key={m.snapshot_month} style={s.tr}>
                  <td style={s.td}>{m.shortLabel}</td>
                  <td style={s.td}>{fmtDate(m.snapshot_as_of_date)}</td>
                  <td style={s.tdR}>{fmtNumber(m.item_count)}</td>
                  <td style={s.tdR}>{fmtNumber(m.distinct_part_count)}</td>
                  <td style={s.tdR}>{fmtMoney(m.total_value)}</td>
                  {/* The first row has no prior snapshot, so it gets an em
                      dash rather than a zero — "no change" and "nothing to
                      compare with" are different statements. */}
                  <td style={{
                    ...s.tdR,
                    color: change === null ? c.textFaint : (change > 0 ? c.warningText : c.textStrong),
                  }}>
                    {change === null ? '—' : `${change > 0 ? '+' : ''}${fmtMoney(change)}`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </DataDisclosure>
    </div>
  );
}

const s = {
  wrap:       { background: '#F9FAFB' },
  header:     { display: 'flex', alignItems: 'baseline', gap: '12px', padding: '16px 18px 0', flexWrap: 'wrap' },
  title:      { fontSize: '14px', fontWeight: 700, color: c.navy },
  subtitle:   { fontSize: '11px', color: '#9CA3AF' },
  chartWrap:  { padding: '10px 18px 4px' },
  strip: {
    display: 'flex', borderTop: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB',
    background: '#fff',
  },
  stripItem:  { flex: 1, padding: '10px 14px', borderRight: '1px solid #F3F4F6' },
  stripLabel: { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#9CA3AF', marginBottom: '2px' },
  stripVal:   { fontSize: '13px', fontWeight: 600, color: '#111827', fontVariantNumeric: 'tabular-nums' },
  empty:      { padding: '32px', textAlign: 'center', color: '#9CA3AF', fontSize: '13px' },
  err:        { padding: '16px', color: '#DC2626', fontSize: '13px' },

  table: { width: '100%', borderCollapse: 'collapse', background: c.surface },
  th:    { ...tbl.th,  whiteSpace: 'nowrap' },
  thR:   { ...tbl.thR, whiteSpace: 'nowrap' },
  tr:    { ...tbl.tr },
  td:    { ...tbl.td,  whiteSpace: 'nowrap' },
  tdR:   { ...tbl.tdR },
};
